/** @jsxImportSource ./simboluri-jsx */
import { useMemo, useState } from "react";
import { t, useT, useTn } from "../i18n";
import type { Cheie } from "../i18n";
import { EfortRand, EforturiResult, StepState, fmtDate } from "./tipuri";
import { eforturiCsvUrl } from "./apiStructura";
import ModeleRigiditate from "./ModeleRigiditate";
import Info from "./Info";
import { DescriereaPasului } from "./TitluPas";
import TopSolicitari from "./TopSolicitari";
import { numelePunctului } from "./puncte";

// Pasul 12 — Eforturile finale: tabelul M/V/N per element (livrabilul
// principal). Fiecare grindă are 5 puncte de verificare (fețele reazemelor
// sus/jos + câmpul), fiecare stâlp baza și vârful cu extremele concomitente
// (P_min/P_max/M2_max/M3_max), separat pe gruparea fundamentală și pe cea
// seismică — plus reacțiunile la bază pentru fundații și CSV-ul complet.

/** explicația unui punct de verificare, cu căderea pe chiar cheia lui */
const puncte = numelePunctului;

// Ce înseamnă fiecare coloană a tabelelor — afișat ca tooltip pe cap de
// tabel și în legenda de sub tabele.
const COL_HELP: Record<string, Cheie> = {
  Element: "struct.ef.h_element",
  Nivel: "struct.ef.h_nivel",
  Grupare: "struct.ef.h_grupare",
  Punct: "struct.ef.h_punct",
  Criteriu: "struct.ef.h_criteriu",
  x: "struct.ef.h_x",
  Combinația: "struct.ef.h_combinatia",
  P: "struct.ef.h_P",
  V2: "struct.ef.h_V2",
  "V2,GS": "struct.ef.h_V2GS",
  V3: "struct.ef.h_V3",
  T: "struct.ef.h_T",
  M2: "struct.ef.h_M2",
  M3: "struct.ef.h_M3",
  L: "struct.ef.h_L",
  "l₀": "struct.ef.h_l0",
};

/** ajutorul unei coloane; capul de tabel rămâne notația de desen */
const ajutor = (c: string) => (COL_HELP[c] ? t(COL_HELP[c]) : c);

// Capetele care sunt CUVINTE se traduc; «P», «V2», «M3», «x», «L», «l₀» sunt
// notații de desen și rămân cum sunt.
const COL_ETICHETA: Record<string, Cheie> = {
  Element: "verificari.col_element",
  Nivel: "struct.top.nivel",
  Grupare: "struct.ef.c_grupare",
  Punct: "struct.ef.c_punct",
  Criteriu: "struct.ef.c_criteriu",
  Combinația: "struct.ef.c_combinatia",
};

function Th({ c }: { c: string }) {
  const tr = useT();
  return <th title={ajutor(c)}>{COL_ETICHETA[c] ? tr(COL_ETICHETA[c]) : c}</th>;
}

function Legenda({ cols }: { cols: string[] }) {
  const t = useT();
  return (
    <details style={{ marginTop: 8 }}>
      <summary className="sg-muted" style={{ cursor: "pointer" }}>
        {t("struct.ef.legenda")}
      </summary>
      <table className="sg-table" style={{ marginTop: 6 }}>
        <tbody>
          {cols.map((c) => (
            <tr key={c}>
              <td className="sg-mono" style={{ whiteSpace: "nowrap" }}>
                <strong>{COL_ETICHETA[c] ? t(COL_ETICHETA[c]) : c}</strong>
              </td>
              <td className="sg-muted">{COL_HELP[c] ? t(COL_HELP[c]) : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}

function cmpElement(a: EfortRand, b: EfortRand): number {
  return (
    a.element.localeCompare(b.element, undefined, { numeric: true }) ||
    a.nivel.localeCompare(b.nivel, undefined, { numeric: true }) ||
    a.loc_m - b.loc_m
  );
}

export default function StepEforturi({
  projectId,
  cloneId = null,
  rutaId,
  solutie,
  state,
  running,
}: {
  projectId: string;
  // clona soluției vizualizate din care se citesc datele; null = proiectul
  cloneId?: string | null;
  // id-ul din URL (proiectul REAL) + soluția vizualizată: linkurile spre
  // paginile de element trebuie să poarte perechea asta, nu clona
  rutaId?: string;
  solutie?: string | null;
  state: StepState | null;
  running: boolean;
}) {
  const t = useT();
  if (running) {
    return (
      <div className="st-status">
        <div className="st-spinner" />
        <div>
          <strong>{t("struct.pas.ruleaza")}</strong>
          <div className="sg-muted">{t("struct.ef.ruleaza")}</div>
        </div>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="sg-muted st-empty">
        {t("struct.ef.nerulat")}
        {/* descrierea vine din catalog_pasi.py — același text cu
            cel de la hover pe titlul pasului */}
        <DescriereaPasului cheie="eforturi" />
      </div>
    );
  }

  const r = state.result as unknown as EforturiResult;

  if (state.status === "error") {
    return (
      <div>
        <div className="sg-error">
          {t("struct.pas.esuat", {
            eroare: r?.error ?? t("struct.pas.eroare_necunoscuta"),
          })}
        </div>
        <div className="sg-muted">{t("struct.pas.ruleaza_din_nou")}</div>
      </div>
    );
  }

  return (
    <EforturiView
      projectId={projectId}
      cloneId={cloneId}
      rutaId={rutaId ?? projectId}
      solutie={solutie}
      r={r}
      updatedAt={state.updated_at}
    />
  );
}

// ---------------------------------------------------------------------------
// Vizualizarea propriu-zisă (rezultat valid)
// ---------------------------------------------------------------------------

type Tab = "grinzi" | "stalpi" | "reactiuni";

function EforturiView({
  projectId,
  cloneId = null,
  rutaId,
  solutie,
  r,
  updatedAt,
}: {
  projectId: string;
  // clona soluției vizualizate din care se descarcă CSV-ul; null = proiectul
  cloneId?: string | null;
  rutaId: string;
  solutie?: string | null;
  r: EforturiResult;
  updatedAt: string;
}) {
  const t = useT();
  const tn = useTn();
  const [tab, setTab] = useState<Tab>("grinzi");
  const [nivel, setNivel] = useState<string>("");
  const [grupa, setGrupa] = useState<string>("");
  const [cauta, setCauta] = useState<string>("");

  const niveluri = useMemo(() => {
    const s = new Set<string>();
    r.grinzi.forEach((g) => s.add(g.nivel));
    r.stalpi.forEach((g) => s.add(g.nivel));
    return Array.from(s).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [r]);

  const filtreaza = (rows: EfortRand[]) =>
    rows
      .filter((x) => !nivel || x.nivel === nivel)
      .filter((x) => !grupa || x.grupa === grupa)
      .filter((x) => !cauta || x.element.toLowerCase().includes(cauta.trim().toLowerCase()))
      .sort(cmpElement);

  const grinzi = useMemo(
    () => filtreaza(r.grinzi),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [r, nivel, grupa, cauta]
  );
  const stalpi = useMemo(
    () => filtreaza(r.stalpi),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [r, nivel, grupa, cauta]
  );

  return (
    <div className="st-result">
      <ModeleRigiditate pas={12} r={r} />
      {/* întâi răspunsul la „ce e cel mai solicitat" — restul rândurilor
          rămân în tabelele filtrabile de mai jos */}
      <TopSolicitari
        rutaId={rutaId}
        solutie={solutie}
        grinzi={r.grinzi ?? []}
        stalpi={r.stalpi ?? []}
        nrCombinatii={r.nr_combinatii}
      />
      <div className="st-cards">
        <div className="st-card st-card-main">
          <div className="st-card-label">
            {t("struct.ef.livrabil")}
            <Info
              titlu={t("struct.ef.info_titlu")}
              ce={t("struct.ef.info_ce")}
              deCe={t("struct.ef.info_de_ce")}
              parametri={[
                { simbol: "M3", nume: t("struct.ef.p_M3"), sursa: t("struct.ef.p_M3_sursa") },
                { simbol: "V2", nume: t("struct.ef.p_V2"), sursa: t("struct.ef.p_idem") },
                { simbol: "P", nume: t("struct.ef.p_P"), sursa: t("struct.ef.p_idem") },
                { simbol: "V2,GS", nume: t("struct.ef.p_V2GS"), sursa: t("struct.ef.p_V2GS_sursa") },
                { simbol: "l₀", nume: t("struct.ef.p_l0"), sursa: t("struct.ef.p_l0_sursa") },
              ]}
            />
          </div>
          <div className="st-card-value">
            {r.nr_grinzi} + {r.nr_stalpi}
          </div>
          <div className="st-card-sub">
            {t("struct.ef.sub", { n: r.nr_combinatii })}
          </div>
        </div>
        <div className="st-card">
          <div className="st-card-label">{t("struct.ef.export")}</div>
          <div className="st-card-value" style={{ fontSize: 22 }}>CSV</div>
          <div className="st-card-sub">
            {t("struct.ef.export_sub", { fisier: r.csv })}
          </div>
          <div style={{ marginTop: 8 }}>
            <a className="sg-primary" style={{ textDecoration: "none", display: "inline-block", padding: "7px 14px", borderRadius: 6 }} href={eforturiCsvUrl(projectId, cloneId)}>
              {t("struct.ef.descarca")}
            </a>
          </div>
        </div>
      </div>

      {r.avertismente && r.avertismente.length > 0 && (
        <div className="st-findings">
          <h3>{tn("struct.ef.sanatate#n", r.avertismente.length)}</h3>
          {r.avertismente.map((a, i) => (
            <div key={i} className="st-warn">• {a}</div>
          ))}
        </div>
      )}

      <div className="st-findings">
        <div className="md-tabs">
          <button
            className={`md-tab ${tab === "grinzi" ? "active" : ""}`}
            onClick={() => setTab("grinzi")}
          >
            {t("struct.ef.tab_grinzi", { n: r.nr_grinzi })}
          </button>
          <button
            className={`md-tab ${tab === "stalpi" ? "active" : ""}`}
            onClick={() => setTab("stalpi")}
          >
            {t("struct.ef.tab_stalpi", { n: r.nr_stalpi })}
          </button>
          <button
            className={`md-tab ${tab === "reactiuni" ? "active" : ""}`}
            onClick={() => setTab("reactiuni")}
          >
            {t("struct.ef.tab_reactiuni", { n: r.reactiuni_baza.length })}
          </button>
        </div>

        {tab !== "reactiuni" && (
          <div className="ef-filters" style={{ display: "flex", gap: 10, alignItems: "center", margin: "10px 0", flexWrap: "wrap" }}>
            <label className="sg-muted">
              {t("struct.top.nivel")}{" "}
              <select value={nivel} onChange={(e) => setNivel(e.target.value)}>
                <option value="">{t("struct.ef.toate")}</option>
                {niveluri.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </label>
            <label className="sg-muted">
              {t("struct.ef.c_grupare")}{" "}
              <select value={grupa} onChange={(e) => setGrupa(e.target.value)}>
                <option value="">{t("struct.ef.toate")}</option>
                <option value="fundamentala">{t("struct.ef.fundamentala")}</option>
                <option value="seismica">{t("struct.ef.seismica")}</option>
              </select>
            </label>
            <label className="sg-muted">
              {t("verificari.col_element")}{" "}
              <input
                type="text"
                placeholder={t("struct.ef.cauta")}
                value={cauta}
                onChange={(e) => setCauta(e.target.value)}
                style={{ padding: "4px 8px", border: "1px solid #c8c8c8", borderRadius: 5, fontSize: 13 }}
              />
            </label>
          </div>
        )}

        {tab === "grinzi" && <TabelGrinzi rows={grinzi} />}
        {tab === "stalpi" && <TabelStalpi rows={stalpi} />}
        {tab === "reactiuni" && <TabelReactiuni r={r} />}
      </div>

      <div className="st-findings">
        <h3>{t("struct.ef.conventii")}</h3>
        {r.conventii.map((c, i) => (
          <div key={i} className="sg-muted" style={{ marginBottom: 4 }}>• {c}</div>
        ))}
      </div>

      <div className="sg-muted st-ran-at">
        {t("struct.pas.rulat_la", { cand: fmtDate(updatedAt) })}
      </div>
    </div>
  );
}

const COLS_GRINZI = ["Element", "Nivel", "Grupare", "Punct", "x", "Combinația",
  "P", "V2", "V2,GS", "V3", "T", "M2", "M3", "L", "l₀"];

function TabelGrinzi({ rows }: { rows: EfortRand[] }) {
  const t = useT();
  if (rows.length === 0)
    return <div className="sg-muted">{t("struct.ef.fara_randuri")}</div>;
  return (
    <div style={{ overflowX: "auto" }}>
      <table className="sg-table st-strat">
        <thead>
          <tr>
            <Th c="Element" />
            <Th c="Nivel" />
            <Th c="Grupare" />
            <Th c="Punct" />
            <Th c="x" />
            <Th c="Combinația" />
            <th title={ajutor("P")}>P (kN)</th>
            <th title={ajutor("V2")}>V2 (kN)</th>
            <th title={ajutor("V2,GS")}>V2,GS (kN)</th>
            <th title={ajutor("V3")}>V3 (kN)</th>
            <th title={ajutor("T")}>T (kN·m)</th>
            <th title={ajutor("M2")}>M2 (kN·m)</th>
            <th title={ajutor("M3")}>M3 (kN·m)</th>
            <Th c="L" />
            <Th c="l₀" />
          </tr>
        </thead>
        <tbody>
          {rows.map((g, i) => (
            <tr key={i}>
              <td className="sg-mono"><strong>{g.element}</strong></td>
              <td className="sg-mono">{g.nivel}</td>
              <td className="sg-muted">{g.grupa}</td>
              <td className="sg-mono" title={puncte(g.punct)}>{g.punct}</td>
              <td className="sg-mono">{g.loc_m} m</td>
              <td className="sg-mono">{g.combinatie}</td>
              <td className="sg-mono">{g.P_kn}</td>
              <td className="sg-mono">{g.V2_kn}</td>
              <td className="sg-mono">{g.V2_gs_kn ?? "—"}</td>
              <td className="sg-mono">{g.V3_kn}</td>
              <td className="sg-mono">{g.T_knm}</td>
              <td className="sg-mono">{g.M2_knm}</td>
              <td className="sg-mono"><strong>{g.M3_knm}</strong></td>
              <td className="sg-mono">{g.L_m} m</td>
              <td className="sg-mono">{g.l0_m ?? "—"} m</td>
            </tr>
          ))}
        </tbody>
      </table>
      <Legenda cols={COLS_GRINZI} />
    </div>
  );
}

function TabelStalpi({ rows }: { rows: EfortRand[] }) {
  const t = useT();
  if (rows.length === 0)
    return <div className="sg-muted">{t("struct.ef.fara_randuri")}</div>;
  return (
    <div style={{ overflowX: "auto" }}>
      <table className="sg-table st-strat">
        <thead>
          <tr>
            <Th c="Element" />
            <Th c="Nivel" />
            <Th c="Grupare" />
            <Th c="Punct" />
            <Th c="Criteriu" />
            <Th c="Combinația" />
            <th title={ajutor("P")}>P (kN)</th>
            <th title={ajutor("V2")}>V2 (kN)</th>
            <th title={ajutor("V3")}>V3 (kN)</th>
            <th title={ajutor("T")}>T (kN·m)</th>
            <th title={ajutor("M2")}>M2 (kN·m)</th>
            <th title={ajutor("M3")}>M3 (kN·m)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s, i) => (
            <tr key={i}>
              <td className="sg-mono"><strong>{s.element}</strong></td>
              <td className="sg-mono">{s.nivel}</td>
              <td className="sg-muted">{s.grupa}</td>
              <td className="sg-mono" title={puncte(s.punct)}>{s.punct}</td>
              <td className="sg-mono">{s.criteriu}</td>
              <td className="sg-mono">{s.combinatie}</td>
              <td className="sg-mono"><strong>{s.P_kn}</strong></td>
              <td className="sg-mono">{s.V2_kn}</td>
              <td className="sg-mono">{s.V3_kn}</td>
              <td className="sg-mono">{s.T_knm}</td>
              <td className="sg-mono">{s.M2_knm}</td>
              <td className="sg-mono">{s.M3_knm}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <Legenda cols={["Element", "Nivel", "Grupare", "Punct", "Criteriu",
        "Combinația", "P", "V2", "V3", "T", "M2", "M3"]} />
    </div>
  );
}

function TabelReactiuni({ r }: { r: EforturiResult }) {
  const t = useT();
  if (r.reactiuni_baza.length === 0)
    return <div className="sg-muted">{t("struct.ef.fara_reactiuni")}</div>;
  return (
    <div style={{ overflowX: "auto" }}>
      <table className="sg-table st-strat">
        <thead>
          <tr>
            <th>{t("struct.ef.col_nod")}</th>
            <th>x</th>
            <th>y</th>
            <th>{t("struct.ef.col_comb_fz")}</th>
            <th>FZ (kN)</th>
            <th>MX (kN·m)</th>
            <th>MY (kN·m)</th>
          </tr>
        </thead>
        <tbody>
          {r.reactiuni_baza.map((n) => (
            <tr key={n.nod}>
              <td className="sg-mono"><strong>{n.nod}</strong></td>
              <td className="sg-mono">{n.x_m} m</td>
              <td className="sg-mono">{n.y_m} m</td>
              <td className="sg-mono">{n.combinatie}</td>
              <td className="sg-mono"><strong>{n.FZ_kn}</strong></td>
              <td className="sg-mono">{n.MX_knm}</td>
              <td className="sg-mono">{n.MY_knm}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="sg-muted" style={{ marginTop: 6 }}>
        {t("struct.ef.nota_reactiuni")}
      </div>
    </div>
  );
}
